/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ApiHolder } from '@backstage/core-plugin-api';
import {
  CustomFieldExtensionSchema,
  ScaffolderRJSFFieldProps,
} from '@backstage/plugin-scaffolder-react';
import {
  FieldProps as FieldPropsV5,
  FieldValidation as FieldValidationV5,
  UIOptionsType,
  UiSchema,
  UiSchema as UiSchemaV5,
} from '@rjsf/utils';
import { JSONSchema7 } from 'json-schema';
import { PropsWithChildren } from 'react';
import { z } from 'zod';
import zodToJsonSchema from 'zod-to-json-schema';

/**
 * Type for the Custom Field Extension with the
 * name and components and validation function.
 *
 * @public
 */
export type FieldExtensionOptions<
  TFieldReturnValue = unknown,
  TUiOptions = unknown,
> = {
  name: string;
  component: (
    props: FieldExtensionComponentProps<TFieldReturnValue, TUiOptions>,
  ) => JSX.Element | null;
  validation?: CustomFieldValidator<TFieldReturnValue>;
  asyncValidation?: boolean;
};

/**
 * Field validation type for Custom Field Extensions.
 *
 * @public
 */
export type CustomFieldValidator<TFieldReturnValue> = (
  data: TFieldReturnValue,
  field: any,
  context: { apiHolder: ApiHolder },
  configuration: any,
  formContext: any,
  fieldName?: string,
) => void | Promise<void> | Promise<any>;

/**
 * Type for Field Extension Props for RJSF v5
 *
 * @alpha
 */
export interface NextFieldExtensionComponentProps<
  TFieldReturnValue,
  TUiOptions = {},
> extends PropsWithChildren<FieldPropsV5<TFieldReturnValue>> {
  uiSchema?: UiSchemaV5<TFieldReturnValue> & {
    'ui:options'?: TUiOptions & UIOptionsType;
  };
}

/**
 * Field validation type for Custom Field Extensions.
 *
 * @alpha
 */
export type NextCustomFieldValidator<TFieldReturnValue> = (
  data: TFieldReturnValue,
  field: FieldValidationV5,
  context: { apiHolder: ApiHolder },
) => void | Promise<void>;

/**
 * Type for the Custom Field Extension with the
 * name and components and validation function.
 *
 * @alpha
 */
export type NextFieldExtensionOptions<
  TFieldReturnValue = unknown,
  TInputProps = unknown,
> = {
  name: string;
  component: (
    props: NextFieldExtensionComponentProps<TFieldReturnValue, TInputProps>,
  ) => JSX.Element | null;
  validation?: NextCustomFieldValidator<TFieldReturnValue>;
};

/**
 * Type for 'ui:filter' parameter.
 */
export interface UiFilter {
  fieldName: string;
  entityPath: string;
}

/**
 * Type for witboost UiSchema.
 */
export interface WmUiSchema extends UiSchema {
  'ui:filter'?: UiFilter[];
  'ui:property'?: string;
  'ui:fieldName'?: string;
}

enum ApiMethod {
  POST = 'POST',
  GET = 'GET',
  PATCH = 'PATCH',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

export interface ApiSpec {
  microserviceId: string;
  baseUrl?: string;
  path?: string;
  method?: ApiMethod;
  params?: any;
}

export interface MicroserviceSpec {
  retrieval: ApiSpec;
  validation: ApiSpec;
}

export type GenericApiSpec = {
  retrieval: {
    baseUrl: string;
    path: string;
    method?: string;
    params?: Record<string, any>;
  };
};
export type uiOptions = {
  fieldsToSave?: string | string[];
  displayFields?: string | string[];
  selectedField?: string | string[];
  apiSpec?: MicroserviceSpec | GenericApiSpec;
  aiEnabled?: boolean;
};

export interface FieldExtensionUiSchema<TFieldReturnValue, TUiOptions>
  extends UiSchema<TFieldReturnValue> {
  'ui:options'?: TUiOptions & UIOptionsType<TFieldReturnValue>;
  'ui:filter'?: UiFilter[];
  'ui:property'?: string;
  'ui:fieldName'?: string;
  'ui:fieldsToSave'?: string[];
  'ui:displayFields'?: string[];
  'ui:apiSpec'?: MicroserviceSpec;
}

/** @public */
export function makeFieldSchema<
  TReturnType extends z.ZodType,
  TUiOptions extends z.ZodType,
>(options: {
  output: (zImpl: typeof z) => TReturnType;
  uiOptions?: (zImpl: typeof z) => TUiOptions;
}): FieldSchema<z.output<TReturnType>, z.output<TUiOptions>> {
  const { output, uiOptions } = options;
  return {
    TProps: undefined as any,
    TOutput: undefined as any,
    schema: {
      returnValue: zodToJsonSchema(output(z)) as JSONSchema7,
      uiOptions: uiOptions && (zodToJsonSchema(uiOptions(z)) as JSONSchema7),
    },

    // These will be removed - just here for backwards compat whilst we're moving across
    type: undefined as any,
    uiOptionsType: undefined as any,
  };
}

/**
 * @public
 * FieldSchema encapsulates a JSONSchema7 along with the
 * matching FieldExtensionComponentProps type for a field extension.
 */
export interface FieldSchema<TReturn, TUiOptions> {
  /** @deprecated use TProps instead */
  readonly type: FieldExtensionComponentProps<TReturn, TUiOptions>;
  /** @deprecated will be removed */
  readonly uiOptionsType: TUiOptions;

  readonly schema: CustomFieldExtensionSchema;
  readonly TProps: FieldExtensionComponentProps<TReturn, TUiOptions>;
  readonly TOutput: TReturn;
}

// here we define extra custom props pickers can use, the only way to provide this properties is to inject them manually using cloneElement to modify props from templates,
// (for example like `__inTableRowTemplate` in TableRowTemplate.tsx)
export interface CommonPickerProps {
  __inTableRowTemplate?: true;
}

/**
 * Type for Field Extension Props for RJSF v5
 *
 * @public
 */
export interface FieldExtensionComponentProps<
  TFieldReturnValue,
  TUiOptions = {},
> extends PropsWithChildren<
    ScaffolderRJSFFieldProps<TFieldReturnValue> & CommonPickerProps
  > {
  uiSchema: FieldExtensionUiSchema<TFieldReturnValue, TUiOptions>;
}
